import { useMemo } from 'react';
import { ProjectId } from 'api/models';
import { Task as TaskApi } from 'api/tasks';
import {
  PhaseEntityType,
  converToEntityId,
} from 'components/organisms/projects/project-plan/phase-entity';
import { Task } from 'data/tasks';
import { useTasksFromStoreRQ } from 'data/tasks/hooks';
import { useProjectPhaseByProjectId } from './use-project-phase.store';

type TaskObject = Pick<
  TaskApi,
  'startDate' | 'dueDate' | 'assignee' | 'dependentTasks' | 'private'
> & {
  level: number;
  parentTotal: number | undefined;
  children: TaskObject[];
  key: string;
  id: number;
  title: string;
  data: Task;
  rootIndex: number;
  type: string;
};

const frameTaskObject = (task, rootIndex) => ({
  key: `TASK-${task.taskId}`,
  id: task.taskId,
  title: task.taskName,
  startDate: task.startDate,
  dueDate: task.dueDate,
  assignee: task.assignee,
  private: task.private,
  dependentTasks: task.dependentTasks ?? [],
  data: task,
  rootIndex,
});

const populateChildren = (
  task: Task | undefined,
  index: number,
  level: number,
  allTasks: Task[],
) => {
  if (!task) {
    return [] as any[];
  } // Return an empty array if task is undefined

  const children: any[] = [];

  // Check if the task has subtasks
  if (task.subTaskOrder && task.subTaskOrder.length > 0) {
    children.push(
      ...task.subTaskOrder.reduce((acc: TaskObject[], subtaskId) => {
        const subTask = allTasks.find((_task) => _task.taskId === subtaskId);

        if (subTask) {
          acc.push({
            ...frameTaskObject(subTask, index),
            type: 'TASK',
            level: level + 1,
            parentTotal: task?.subTaskOrder?.length,
            children: populateChildren(subTask, index, level + 1, allTasks), // Recursively populate children
          });
        }

        return acc;
      }, []),
    );
  }

  return children;
};

export const useTransformTabularData = (projectId: ProjectId) => {
  const projectPhases = useProjectPhaseByProjectId(projectId[1]);
  const allTasks = useTasksFromStoreRQ(ProjectId(projectId[1]));

  const tabData = useMemo(() => {
    return projectPhases.map((projectPhase, index) => {
      return {
        key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
        id: projectPhase.projectPhaseId,
        title: projectPhase.projectPhaseName,
        startDate: projectPhase.startDate,
        dueDate: projectPhase.dueDate,
        type: 'PROJECT_PHASE',
        private: projectPhase.private,
        level: 0,
        data: projectPhase,
        rootIndex: index,
        parentTotal: 0,
        dependentProjectPhases: projectPhase.dependentProjectPhases ?? [],
        entityOrder: projectPhase.entityOrder ?? [],
        children:
          projectPhase?.entityOrder === undefined
            ? []
            : projectPhase.entityOrder.reduce(
                (accumulator: TaskObject[], id) => {
                  const task = allTasks.find(
                    (_task) =>
                      converToEntityId(_task.taskId, PhaseEntityType.Task) ===
                      id,
                  );

                  if (task) {
                    accumulator.push({
                      ...frameTaskObject(task, index),
                      type: 'TASK',
                      level: 1,
                      parentTotal: projectPhase?.entityOrder?.length,
                      children: populateChildren(task, index, 1, allTasks),
                    });
                  }

                  return accumulator;
                },
                [],
              ),
      };
    });
  }, [projectPhases, allTasks]);

  return tabData;
};
